/**
 * CatalogAdminTab v3 — минималистичный дизайн.
 * Один экран: дерево Раздел → Ветка → Подтип → SKU.
 * Никаких вложенных аккордеонов.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import api, {
  formatAxiosAdminError,
  parseAdminProductsResponse,
  bulkAssignSubtype,
  getAdminCatalogSystemsBySubtype,
  bulkToggleActive,
  uploadCatalogSystemImage,
  assignProductSubtype,
  getAdminCatalogBranches,
  getAdminCatalogSubtypes,
  getAdminSubtypeSkus,
  patchAdminCatalogSubtype,
  patchAdminCatalogBranch,
  createAdminCatalogBranch,
  createAdminCatalogSubtype,
  deleteAdminCatalogBranch,
  deleteAdminCatalogSubtype,
  reorderAdminBranches,
  reorderAdminSubtypes,
  reorderAdminCatalogSystems,
  initBranchesSortOrder,
  uploadSubtypeImage,
  bulkAssignByCategory,
  backfillCatalogSubtypes,
  flushCatalogCache,
  type AdminCatalogBranchRow,
  type AdminCatalogSubtypeRow,
  type AdminVitrineBundle,
  type AdminCatalogSystem,
} from '@/api';
import { useHaptic } from '@/hooks/useHaptic';
import { CATEGORY_ICON } from '@/catalogTaxonomy';
import {
  ChevronRight, Plus, Trash2, Pencil, Check, X,
  RefreshCw, Search, Loader2, GripVertical, ImagePlus, Send,
  Settings2, AlertTriangle, Package, Eye, EyeOff, ChevronLeft, SlidersHorizontal,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { VitrineCategoryPanel } from './VitrineCategoryPanel';
import { SystemEditor } from './SystemEditor';
import { AccessoriesAdminPanel } from './AccessoriesAdminPanel';
import { ServicesAdminPanel } from './ServicesAdminPanel';
import { BulkPriceModal } from './BulkPriceModal';
import { ActivityLogPanel } from './ActivityLogPanel';
import { AdminCatalogGuide } from './AdminCatalogGuide';
import { bindAdminImageCrop } from '@/hooks/useAdminImageCrop';
import { SubtypeSetupPanel } from './SubtypeSetupPanel';
import {
  subtypeFormToSchema,
  subtypeSchemaToForm,
  type SubtypeOptionSchema,
} from './subtypeOptionSchema';
// Dialogs are defined at the bottom of this file
import { PROTOCOL_THEME } from '@/protocolTheme';
import { AdminHorizontalTabRail } from '@/components/admin/AdminHorizontalTabRail';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// ── Types ──────────────────────────────────────────────────────────────────
export interface AdminProduct {
  id: number; external_id?: string; subtype_id?: number | null;
  category: string; subcategory?: string; name: string; description?: string;
  image_url?: string; is_active: boolean;
  price_matrix?: Array<Record<string, unknown>>;
  specs?: Record<string, unknown>;
  protocol?: string | null; color?: string | null;
  motor_full_name?: string | null; motor_model?: string | null;
  interface_type?: string | null; control_types?: string[] | null;
  is_battery_powered?: boolean;
}

type ProductDraft = Partial<AdminProduct> & { base_price?: number };
const ALL_PROTOCOLS = Object.keys(PROTOCOL_THEME);
function readBasePrice(pm?: Array<Record<string, unknown>>) {
  const v = Number(pm?.[0]?.price ?? 0); return Number.isFinite(v) ? v : 0;
}
function withBasePrice(pm: Array<Record<string, unknown>> | undefined, base: number) {
  const n = Math.max(0, Number(base || 0));
  if (!pm?.length) return [{ length_m: 1.0, price: n }];
  return pm.map((r, i) => i === 0 ? { ...r, price: n } : r);
}
function sleepMs(ms: number) { return new Promise<void>(r => setTimeout(r, ms)); }

// ── Section tabs ────────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'catalog',     emoji: '📦', label: 'Каталог'  },
  { id: 'accessories', emoji: '🕹️', label: 'Пульты'  },
  { id: 'services',    emoji: '🔧', label: 'Услуги'   },
  { id: 'vitrine',     emoji: '🛍',  label: 'Витрина' },
  { id: 'history',     emoji: '🕐',  label: 'История' },
] as const;
type SectionId = typeof SECTIONS[number]['id'];

// ── Props ───────────────────────────────────────────────────────────────────
interface Props {
  token: string;
  catalogWrite: boolean;
  allBranches: AdminCatalogBranchRow[];
  allSubtypes: AdminCatalogSubtypeRow[];
  subtypesForBranch: Record<number, AdminCatalogSubtypeRow[]>;
}

// ── Sortable item: Branch (Level 1) ─────────────────────────────────────────
function SortableBranchItem({
  branch: b, catalogWrite, editBranchId, editBranchTitle,
  setEditBranchTitle, setEditBranchId, saveBranchTitle, loadSubtypes, deleteBranch,
}: {
  branch: AdminCatalogBranchRow; catalogWrite: boolean;
  editBranchId: number | null; editBranchTitle: string;
  setEditBranchTitle: (v: string) => void; setEditBranchId: (id: number | null) => void;
  saveBranchTitle: (id: number) => Promise<void>;
  loadSubtypes: (b: AdminCatalogBranchRow) => Promise<void>;
  deleteBranch: (b: AdminCatalogBranchRow) => Promise<void>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: b.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={{ ...style, border: '1px solid rgba(255,255,255,0.06)' }}
      className="flex items-center gap-2 px-3 py-2.5 rounded-xl transition-colors hover:bg-white/[0.04]">
      {editBranchId === b.id ? (
        <div className="flex items-center gap-2 flex-1">
          <input autoFocus value={editBranchTitle}
            className="input-glass flex-1 px-2 py-1.5 rounded-xl text-sm"
            onChange={e => setEditBranchTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void saveBranchTitle(b.id); if (e.key === 'Escape') setEditBranchId(null); }} />
          <button type="button" onClick={() => void saveBranchTitle(b.id)}
            className="press p-1.5 rounded-lg" style={{ background: 'rgba(72,187,120,0.2)', color: '#68D391' }}>
            <Check size={13} />
          </button>
          <button type="button" onClick={() => setEditBranchId(null)}
            className="press p-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}>
            <X size={13} />
          </button>
        </div>
      ) : (
        <>
          {catalogWrite && (
            <button type="button" {...attributes} {...listeners}
              className="touch-none cursor-grab active:cursor-grabbing p-1 rounded flex-shrink-0"
              style={{ color: 'rgba(255,255,255,0.25)' }}>
              <GripVertical size={14} />
            </button>
          )}
          <button type="button" onClick={() => void loadSubtypes(b)}
            className="press flex-1 flex items-center gap-3 text-left min-w-0">
            <span className="text-lg">{CATEGORY_ICON[b.title] || '📦'}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{b.title}</p>
              <p className="text-[10px] flex items-center gap-1.5" style={{ color: 'var(--text-3)' }}>
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{
                    background:
                      b.health === 'ok'
                        ? '#68D391'
                        : b.health === 'empty_on_vitrine'
                          ? '#F6AD55'
                          : b.health === 'hidden'
                            ? '#718096'
                            : '#FC8181',
                  }}
                  aria-hidden
                />
                {b.subtype_count} подтипов
                {b.show_on_vitrine === false ? ' · скрыта' : ''}
              </p>
            </div>
            <ChevronRight size={14} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
          </button>
          {catalogWrite && (
            <div className="flex gap-1 flex-shrink-0">
              <button type="button"
                onClick={() => { setEditBranchId(b.id); setEditBranchTitle(b.title); }}
                className="press p-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.07)', color: 'var(--text-3)' }}>
                <Pencil size={12} />
              </button>
              <button type="button" onClick={() => void deleteBranch(b)}
                className="press p-1.5 rounded-lg" style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A' }}>
                <Trash2 size={12} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function sortSystemsByOrder(systems: AdminCatalogSystem[]): AdminCatalogSystem[] {
  return [...systems].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.title.localeCompare(b.title, 'ru'),
  );
}

function SortableSystemRow({
  item,
  catalogWrite,
  onEdit,
  onDelete,
  onPhotoUpload,
}: {
  item: AdminCatalogSystem;
  catalogWrite: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onPhotoUpload: (file: File) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.55 : 1 };
  return (
    <div ref={setNodeRef} style={style}
      className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/10 transition-colors">
      {catalogWrite && (
        <button type="button" {...attributes} {...listeners}
          className="touch-none cursor-grab active:cursor-grabbing p-1 rounded flex-shrink-0"
          style={{ color: 'rgba(255,255,255,0.25)' }}
          onClick={e => e.stopPropagation()}>
          <GripVertical size={14} />
        </button>
      )}
      <div className="w-9 h-9 rounded-lg bg-white/[0.06] flex items-center justify-center overflow-hidden flex-shrink-0">
        {(item as { image_url?: string }).image_url ? (
          <img src={(item as { image_url?: string }).image_url} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <Package size={16} className="text-white/30" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-white/90 truncate">{item.title}</p>
        <p className="text-[10px] font-mono truncate" style={{ color: 'var(--cyan)' }}>→ таб в конфигураторе</p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button type="button" onClick={onEdit}
          className="p-2 rounded-lg text-[#63B3ED] hover:bg-[#63B3ED]/10 transition-colors" title="Редактор">
          <Settings2 size={14} />
        </button>
        <label className="p-2 rounded-lg text-white/40 hover:bg-white/5 transition-colors cursor-pointer" title="Фото">
          <ImagePlus size={14} />
          <input type="file" accept="image/*" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) onPhotoUpload(f); }} />
        </label>
        {catalogWrite && (
          <button type="button" onClick={onDelete}
            className="p-2 rounded-lg text-red-400/70 hover:bg-red-500/10 transition-colors" title="Удалить">
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function SortableSubtypeAccordionItem({
  subtype,
  subtypeSystems,
  catalogWrite,
  dndSensors,
  openSubtype,
  toggleSubtypeActive,
  deleteSubtype,
  setActiveSubtype,
  setShowCreateSystem,
  setEditingSystem,
  setDeletingSystem,
  openSystemImageCrop,
  onSystemDragEnd,
}: {
  subtype: AdminCatalogSubtypeRow;
  subtypeSystems: AdminCatalogSystem[];
  catalogWrite: boolean;
  dndSensors: ReturnType<typeof useSensors>;
  openSubtype: (st: AdminCatalogSubtypeRow) => Promise<void>;
  toggleSubtypeActive: (st: AdminCatalogSubtypeRow) => Promise<void>;
  deleteSubtype: (st: AdminCatalogSubtypeRow) => Promise<void>;
  setActiveSubtype: (st: AdminCatalogSubtypeRow | null) => void;
  setShowCreateSystem: (v: boolean) => void;
  setEditingSystem: (s: AdminCatalogSystem | null) => void;
  setDeletingSystem: (s: AdminCatalogSystem | null) => void;
  openSystemImageCrop: (systemId: number, file: File) => void;
  onSystemDragEnd: (subtypeId: number, event: DragEndEvent) => void;
}) {
  const hasSystems = subtypeSystems.length > 0;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: subtype.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.55 : 1 };
  const systemIds = subtypeSystems.map((s) => s.id);

  return (
    <div ref={setNodeRef} style={style} className={cn('border-b border-white/[0.06] last:border-0', !subtype.is_active && 'opacity-60')}>
      <AccordionItem value={String(subtype.id)} className="border-0">
        <AccordionTrigger
          className={cn(
            'px-4 py-3 hover:bg-white/[0.02] [&[data-state=open]]:bg-white/[0.03]',
            !hasSystems && subtype.is_active && 'opacity-50',
          )}
        >
          <div className="flex items-center gap-3 text-left flex-1">
            {catalogWrite && (
              <button
                type="button"
                {...attributes}
                {...listeners}
                className="touch-none cursor-grab active:cursor-grabbing p-1 rounded flex-shrink-0"
                style={{ color: 'rgba(255,255,255,0.25)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical size={14} />
              </button>
            )}
            <div
              className={cn(
                'w-2 h-2 rounded-full flex-shrink-0',
                subtype.is_active ? (hasSystems ? 'bg-[#68D391]' : 'bg-white/20') : 'bg-[#FC814A]/60',
              )}
            />
            <span className="text-base">🎭</span>
            <p
              className={cn(
                'flex-1 font-medium text-sm truncate',
                subtype.is_active ? 'text-white/90' : 'text-white/50',
              )}
            >
              {subtype.title}
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void openSubtype(subtype);
              }}
              className="press flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold flex-shrink-0"
              style={{
                background: 'rgba(183,148,244,0.12)',
                color: '#B794F4',
                border: '1px solid rgba(183,148,244,0.28)',
              }}
            >
              <SlidersHorizontal size={11} />
              Настройки
            </button>
            {!subtype.is_active && (
              <span
                className="px-2 py-0.5 rounded-full text-[9px] font-bold"
                style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A' }}
              >
                скрыт
              </span>
            )}
            <span
              className={cn(
                'px-2 py-0.5 rounded-full text-[10px]',
                hasSystems ? 'bg-[#68D391]/15 text-[#68D391]' : 'bg-white/[0.06] text-white/30',
              )}
            >
              {subtypeSystems.length} систем
            </span>
            {catalogWrite && (
              <button
                type="button"
                title={subtype.is_active ? 'Скрыть с витрины' : 'Показать в каталоге'}
                onClick={(e) => {
                  e.stopPropagation();
                  void toggleSubtypeActive(subtype);
                }}
                className="p-1.5 rounded-lg flex-shrink-0 transition-colors"
                style={
                  subtype.is_active
                    ? { color: '#68D391', background: 'rgba(72,187,120,0.12)' }
                    : { color: '#FC814A', background: 'rgba(252,129,74,0.12)' }
                }
              >
                {subtype.is_active ? <Eye size={13} /> : <EyeOff size={13} />}
              </button>
            )}
            {catalogWrite && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void deleteSubtype(subtype);
                }}
                className="p-1 ml-1 mr-1 rounded hover:bg-red-500/20 text-white/20 hover:text-red-400 transition-colors flex-shrink-0"
              >
                <Trash2 size={11} />
              </button>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-3">
          {subtypeSystems.length === 0 ? (
            <div className="flex items-center justify-between py-3">
              <p className="text-xs text-white/40">Нет систем в подтипе</p>
              {catalogWrite && (
                <button
                  type="button"
                  onClick={() => {
                    setActiveSubtype(subtype);
                    setShowCreateSystem(true);
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#68D391]/10 text-[#68D391] hover:bg-[#68D391]/20 transition-colors"
                >
                  <Plus size={11} className="inline mr-1" /> Создать
                </button>
              )}
            </div>
          ) : (
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragEnd={(e) => onSystemDragEnd(subtype.id, e)}
            >
              <SortableContext items={systemIds} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {subtypeSystems.map((item) => (
                    <SortableSystemRow
                      key={item.id}
                      item={item}
                      catalogWrite={catalogWrite}
                      onEdit={() => setEditingSystem(item)}
                      onDelete={() => setDeletingSystem(item)}
                      onPhotoUpload={(f) => openSystemImageCrop(item.id, f)}
                    />
                  ))}
                  {catalogWrite && (
                    <button
                      type="button"
                      onClick={() => {
                        setActiveSubtype(subtype);
                        setShowCreateSystem(true);
                      }}
                      className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs text-[#68D391]/70 hover:text-[#68D391] border border-dashed border-[#68D391]/20 hover:border-[#68D391]/40 transition-colors"
                    >
                      <Plus size={11} /> Добавить систему
                    </button>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </AccordionContent>
      </AccordionItem>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
export function CatalogAdminTab({ token, catalogWrite }: Props) {
  const haptic = useHaptic();
  const { cropModal, openFromFile } = bindAdminImageCrop();
  const [section, setSection] = useState<SectionId>('catalog');
  const [showHiddenSubtypes, setShowHiddenSubtypes] = useState(false);
  const [subtypeAccordionOpen, setSubtypeAccordionOpen] = useState<string[]>([]);
  const [branchesDirty, setBranchesDirty] = useState(false);
  const [subtypesDirty, setSubtypesDirty] = useState(false);
  const [publishingBranches, setPublishingBranches] = useState(false);
  const [publishingSubtypes, setPublishingSubtypes] = useState(false);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  // ── Tree state ─────────────────────────────────────────────────────────
  const [branches, setBranches] = useState<AdminCatalogBranchRow[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [activeBranch, setActiveBranch] = useState<AdminCatalogBranchRow | null>(null);
  const [subtypes, setSubtypes] = useState<AdminCatalogSubtypeRow[]>([]);
  const [subtypesLoading, setSubtypesLoading] = useState(false);
  const [activeSubtype, setActiveSubtype] = useState<AdminCatalogSubtypeRow | null>(null);
  const catalogStep = useMemo((): 1 | 2 | 3 => {
    if (!activeBranch) return 1;
    if (activeSubtype) return 3;
    return 2;
  }, [activeBranch, activeSubtype]);

  // ── Products state ─────────────────────────────────────────────────────
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [catalogListPrimed, setCatalogListPrimed] = useState(false);
  const [catalogVitrineBundle, setCatalogVitrineBundle] = useState<AdminVitrineBundle | 'separate' | null>(null);
  const [productDrafts, setProductDrafts] = useState<Record<number, ProductDraft>>({});
  const [updatingProduct, setUpdatingProduct] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState<number | null>(null);
  const [assigningSubtype, setAssigningSubtype] = useState<number | null>(null);

  // ── Branch/Subtype CRUD state ──────────────────────────────────────────
  const [showNewBranch, setShowNewBranch] = useState(false);
  const [newBranchTitle, setNewBranchTitle] = useState('');
  const [newBranchBusy, setNewBranchBusy] = useState(false);
  const [editBranchId, setEditBranchId] = useState<number | null>(null);
  const [editBranchTitle, setEditBranchTitle] = useState('');
  const [showNewSubtype, setShowNewSubtype] = useState(false);
  const [newSubtypeTitle, setNewSubtypeTitle] = useState('');
  const [newSubtypeBusy, setNewSubtypeBusy] = useState(false);

  // ── Subtype schema state ───────────────────────────────────────────────
  const [schemaForm, setSchemaForm] = useState<SubtypeOptionSchema>({
    steps: [], protocols: [], colors: [], dim_label: '', tile_hint: '', step_copy: {},
  });
  const [systemIdDraft, setSystemIdDraft] = useState('');
  const [skuTotal, setSkuTotal] = useState(0);
  const [newColorInput, setNewColorInput] = useState('');
  const [schemaSaving, setSchemaSaving] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const [assignByCatBusy, setAssignByCatBusy] = useState(false);
  const [catAssignInput, setCatAssignInput] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [formulaDraft, setFormulaDraft] = useState('');
  const [formulaPreview, setFormulaPreview] = useState<string | null>(null);
  const [subtypeImgUploading, setSubtypeImgUploading] = useState(false);
  const subtypeImgRef = useRef<HTMLInputElement | null>(null);

  // ── CatalogSystem (cards v2) state ────────────────────────────────────
  const [catalogSystems, setCatalogSystems] = useState<AdminCatalogSystem[]>([]);
  const [editingSystem, setEditingSystem] = useState<AdminCatalogSystem | null>(null);
  const [deletingSystem, setDeletingSystem] = useState<AdminCatalogSystem | null>(null);
  const [showCreateSystem, setShowCreateSystem] = useState(false);

  // ── Subtype SKU list (replaces flat products when subtype selected) ────
  const [subtypeSkus, setSubtypeSkus] = useState<AdminProduct[]>([]);
  const [subtypeSkusLoading, setSubtypeSkusLoading] = useState(false);

  // ── Load branches (+ auto-backfill if empty + auto-init sort_order) ──────
  const loadBranches = useCallback(async () => {
    if (!token) return;
    setBranchesLoading(true);
    try {
      let rows = await getAdminCatalogBranches();
      if (!Array.isArray(rows) || rows.length === 0) {
        // First time — auto-backfill from real catalog
        setSyncBusy(true);
        try {
          await backfillCatalogSubtypes();
          rows = await getAdminCatalogBranches();
          setSyncResult('✓ Каталог синхронизирован');
          setTimeout(() => setSyncResult(null), 4000);
        } catch { /* non-critical */ }
        finally { setSyncBusy(false); }
      }
      // Auto-init: if all sort_orders identical OR any branch has a Latin title (garbage duplicates)
      if (Array.isArray(rows) && rows.length > 1) {
        const orders = new Set(rows.map(r => r.sort_order));
        const hasLatinBranch = rows.some(r => /^[a-zA-Z\s\-]+$/.test(r.title ?? ''));
        if (orders.size === 1 || hasLatinBranch) {
          try {
            await initBranchesSortOrder();
            rows = await getAdminCatalogBranches();
          } catch { /* non-critical */ }
        }
      }
      setBranches(Array.isArray(rows) ? rows : []);
    }
    catch { setBranches([]); }
    finally { setBranchesLoading(false); }
  }, [token]);

  useEffect(() => { void loadBranches(); }, [loadBranches]);

  const loadSubtypes = async (b: AdminCatalogBranchRow) => {
    haptic.selection(); setActiveBranch(b); setActiveSubtype(null); setSubtypes([]);
    setSubtypesLoading(true);
    try { 
      const r = await getAdminCatalogSubtypes(b.id); 
      const items = Array.isArray(r) ? r : [];
      console.log('[CatalogAdmin] Loaded', items.length, 'subtypes for branch', b.slug, '(ID:', b.id, ')');
      items.forEach(st => console.log('  -', st.title, '(system_id:', st.catalog_system_id, ')'));
      setSubtypes(items); 
    }
    catch(e) { 
      console.error('[CatalogAdmin] Failed to load subtypes:', e);
      setSubtypes([]); 
    }
    finally { setSubtypesLoading(false); }
  };

  const openSubtype = async (st: AdminCatalogSubtypeRow) => {
    haptic.selection(); setActiveSubtype(st); setSubtypeSkus([]);
    setSchemaForm(subtypeSchemaToForm((st.option_schema ?? {}) as Record<string, unknown>));
    setSystemIdDraft(st.catalog_system_id != null ? String(st.catalog_system_id) : '');
    setNewColorInput('');
    setSubtypeSkusLoading(true);
    try {
      const sk = await getAdminSubtypeSkus(st.id, { limit: 300 });
      setSkuTotal(sk.total);
      setSubtypeSkus(sk.items as AdminProduct[]);
    }
    catch { setSkuTotal(0); }
    finally { setSubtypeSkusLoading(false); }
  };

  // ── Load products ──────────────────────────────────────────────────────
  const fetchProducts = useCallback(async () => {
    if (!token) return;
    setProductsLoading(true); setProductsError(null); setCatalogListPrimed(false);
    const load = async (iv: boolean) => {
      const r = await api.get('/admin/products', {
        params: { limit: 300, search: productSearch.trim() || undefined, include_vitrine: iv },
      });
      return parseAdminProductsResponse(r.data);
    };
    const apply = (p: ReturnType<typeof parseAdminProductsResponse>) => {
      setProducts(p.products as AdminProduct[]);
      setCatalogVitrineBundle(p.vitrineEmbedded ?? 'separate');
      setCatalogListPrimed(true);
    };
    try { apply(await load(true)); }
    catch (e1) {
      if (axios.isAxiosError(e1) && e1.response?.status === 401) {
        for (let i = 0; i < 4; i++) {
          await sleepMs(250 * (i + 1));
          try { apply(await load(true)); return; } catch { /* retry */ }
        }
      }
      const st = axios.isAxiosError(e1) ? e1.response?.status : undefined;
      if (st !== 401 && st !== 403) { try { apply(await load(false)); return; } catch { /* fallthrough */ } }
      setProducts([]); setCatalogListPrimed(false); setCatalogVitrineBundle(null);
      setProductsError(formatAxiosAdminError(e1, 'Не удалось загрузить каталог.'));
    } finally { setProductsLoading(false); }
  }, [token, productSearch]);

  useEffect(() => {
    if (section === 'catalog') void fetchProducts();
  }, [section, fetchProducts]);

  // SSOT: Load catalog systems with strict subtype filtering
  // Clears stale state when switching tabs ( Quiet Luxury requirement )
  useEffect(() => {
    if (!activeBranch) { 
      setCatalogSystems([]); 
      setSubtypes([]);
      return; 
    }
    
    // Clear stale state immediately
    setCatalogSystems([]);
    setSubtypes([]);
    setSubtypesLoading(true);
    
    // Load subtypes for this branch
    getAdminCatalogSubtypes(activeBranch.id)
      .then(async (subtypesData) => {
        setSubtypes(subtypesData);
        
        // Parallel load all subtypes at once
        const results = await Promise.allSettled(
          subtypesData.map(subtype =>
            getAdminCatalogSystemsBySubtype(subtype.id, {
              includeMotors: false,
              includePriceTiers: false,
            }).then(systems => {
              systems.forEach(sys => {
                sys.subtype_id = subtype.id;
                sys.branch_id = activeBranch.id;
              });
              return systems;
            })
          )
        );
        const allSystems: AdminCatalogSystem[] = results.flatMap(r =>
          r.status === 'fulfilled' ? r.value : []
        );
        setCatalogSystems(allSystems);
        setSubtypesLoading(false);
      })
      .catch((err) => {
        console.error('[CatalogAdmin] Failed to load subtypes:', err);
        setCatalogSystems([]);
        setSubtypesLoading(false);
      });
  }, [activeBranch]);

  // ── Save product ───────────────────────────────────────────────────────
  const saveProduct = async (id: number) => {
    const draft = productDrafts[id]; const current = products.find(p => p.id === id); if (!current) return;
    setUpdatingProduct(id);
    try {
      const payload: Record<string, unknown> = { ...(draft ?? {}) };
      if (draft?.base_price !== undefined)
        payload.price_matrix = withBasePrice(draft.price_matrix ?? current.price_matrix, draft.base_price);
      delete payload.base_price;
      await api.patch(`/admin/products/${id}`, payload);
      await fetchProducts();
    } finally { setUpdatingProduct(null); }
  };

  // ── Filtered products ──────────────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    // When subtype selected — show its own SKU list (from dedicated endpoint)
    if (activeSubtype) {
      const search = productSearch.trim().toLowerCase();
      const list = search
        ? subtypeSkus.filter(p =>
            (p.name ?? '').toLowerCase().includes(search) ||
            (p.external_id ?? '').toLowerCase().includes(search)
          )
        : subtypeSkus;
      return list;
    }
    let list = products;
    if (activeBranch) {
      const ids = new Set(subtypes.map(s => s.id));
      return list.filter(p => p.subtype_id != null && ids.has(p.subtype_id));
    }
    if (productSearch.trim()) {
      const s = productSearch.toLowerCase();
      return list.filter(p =>
        (p.name ?? '').toLowerCase().includes(s) ||
        (p.external_id ?? '').toLowerCase().includes(s) ||
        (p.category ?? '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [products, subtypeSkus, activeSubtype, activeBranch, subtypes, productSearch]);

  // ── Branch CRUD ────────────────────────────────────────────────────────
  const createBranch = async () => {
    if (!newBranchTitle.trim()) return;
    setNewBranchBusy(true);
    try {
      const row = await createAdminCatalogBranch({ title: newBranchTitle.trim() });
      haptic.selection(); setBranches(p => [...p, row]); setNewBranchTitle(''); setShowNewBranch(false);
    } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка')); }
    finally { setNewBranchBusy(false); }
  };

  const saveBranchTitle = async (id: number) => {
    const t = editBranchTitle.trim(); if (!t) return;
    try {
      const u = await patchAdminCatalogBranch(id, { title: t });
      haptic.selection(); setBranches(p => p.map(b => b.id === id ? { ...b, title: u.title } : b)); setEditBranchId(null);
    } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка')); }
  };

  const deleteBranch = async (b: AdminCatalogBranchRow) => {
    if (!window.confirm(`Удалить ветку «${b.title}»? Подтипы будут удалены, SKU потеряют привязку.`)) return;
    try {
      await deleteAdminCatalogBranch(b.id); haptic.selection();
      setBranches(p => p.filter(x => x.id !== b.id));
      if (activeBranch?.id === b.id) { setActiveBranch(null); setSubtypes([]); setActiveSubtype(null); }
    } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка')); }
  };

  // ── Subtype CRUD ───────────────────────────────────────────────────────
  const createSubtype = async () => {
    if (!activeBranch || !newSubtypeTitle.trim()) return;
    setNewSubtypeBusy(true);
    try {
      const row = await createAdminCatalogSubtype(activeBranch.id, { title: newSubtypeTitle.trim() });
      haptic.selection(); setSubtypes(p => [...p, row]);
      setBranches(p => p.map(b => b.id === activeBranch.id ? { ...b, subtype_count: b.subtype_count + 1 } : b));
      setNewSubtypeTitle(''); setShowNewSubtype(false);
    } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка')); }
    finally { setNewSubtypeBusy(false); }
  };

  const deleteSubtype = async (st: AdminCatalogSubtypeRow) => {
    const msg = st.sku_count > 0
      ? `Удалить «${st.title}»? ${st.sku_count} SKU потеряют подтип.`
      : `Удалить «${st.title}»?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteAdminCatalogSubtype(st.id, st.sku_count > 0); haptic.selection();
      setSubtypes(p => p.filter(s => s.id !== st.id));
      setBranches(p => p.map(b => b.id === activeBranch?.id ? { ...b, subtype_count: Math.max(0, b.subtype_count - 1) } : b));
      if (activeSubtype?.id === st.id) setActiveSubtype(null);
    } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка')); }
  };

  // ── Toggle subtype active ──────────────────────────────────────────────
  const toggleSubtypeActive = async (st: AdminCatalogSubtypeRow) => {
    if (!catalogWrite) return;
    try {
      await patchAdminCatalogSubtype(st.id, { is_active: !st.is_active });
      haptic.selection();
      const updated = { ...st, is_active: !st.is_active };
      setSubtypes(p => p.map(s => s.id === st.id ? updated : s));
      if (activeSubtype?.id === st.id) setActiveSubtype(updated);
      void flushCatalogCache().catch(() => undefined);
    } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка')); }
  };

  // ── Save schema ────────────────────────────────────────────────────────
  const saveSchema = async () => {
    if (!catalogWrite || !activeSubtype) return;
    setSchemaSaving(true);
    try {
      const sid = systemIdDraft.trim();
      await patchAdminCatalogSubtype(activeSubtype.id, {
        option_schema: subtypeFormToSchema(schemaForm),
        catalog_system_id: sid === '' ? null : Number(sid),
      });
      haptic.success();
      if (activeBranch) {
        const rows = await getAdminCatalogSubtypes(activeBranch.id);
        setSubtypes(Array.isArray(rows) ? rows : []);
        const updated = rows.find((s: AdminCatalogSubtypeRow) => s.id === activeSubtype.id);
        if (updated) setActiveSubtype(updated);
      }
    } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка сохранения')); }
    finally { setSchemaSaving(false); }
  };

  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);

  // ── Helpers ────────────────────────────────────────────────────────────
  const uploadSubtypeImageFile = async (file: File) => {
    if (!activeSubtype) return;
    setSubtypeImgUploading(true);
    try {
      const res = await uploadSubtypeImage(activeSubtype.id, file);
      haptic.success();
      setSchemaForm((p) => ({ ...p, image_url: res.image_url }));
      setActiveSubtype((prev) =>
        prev ? { ...prev, option_schema: { ...(prev.option_schema ?? {}), image_url: res.image_url } } : prev,
      );
    } catch (err) {
      alert(formatAxiosAdminError(err, 'Ошибка загрузки'));
    } finally {
      setSubtypeImgUploading(false);
      if (subtypeImgRef.current) subtypeImgRef.current.value = '';
    }
  };

  const handleSystemPhotoUpload = async (systemId: number, file: File) => {
    try {
      await uploadCatalogSystemImage(systemId, file);
      haptic.success();
    } catch (err) {
      alert(formatAxiosAdminError(err, 'Ошибка загрузки фото'));
    }
  };

  const openSubtypeImageCrop = async (file: File) => {
    if (!activeSubtype) return;
    await openFromFile({
      file,
      defaultPreset: 'vitrine_tile',
      allowedPresets: ['vitrine_tile', 'catalog_wide', 'square', 'full_size'],
      title: `Фото подтипа · ${activeSubtype.title}`,
      onConfirm: uploadSubtypeImageFile,
      onConfirmRaw: uploadSubtypeImageFile,
    });
  };

  const openSystemImageCrop = async (systemId: number, file: File) => {
    await openFromFile({
      file,
      defaultPreset: 'catalog_wide',
      allowedPresets: ['catalog_wide', 'square', 'full_size', 'vitrine_tile'],
      title: 'Главное фото системы',
      onConfirm: async (cropped: File) => {
        await handleSystemPhotoUpload(systemId, cropped);
      },
      onConfirmRaw: async (raw: File) => {
        await handleSystemPhotoUpload(systemId, raw);
      },
    });
  };
  
  const hiddenSubtypeCount = useMemo(
    () => subtypes.filter((s) => !s.is_active).length,
    [subtypes],
  );

  const subtypesForAccordion = useMemo(() => {
    const sorted = [...subtypes].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.title.localeCompare(b.title, 'ru'),
    );
    return showHiddenSubtypes ? sorted : sorted.filter((s) => s.is_active);
  }, [subtypes, showHiddenSubtypes]);

  const systemsForSubtype = useCallback(
    (subtypeId: number) =>
      sortSystemsByOrder(
        catalogSystems.filter((sys) => sys.subtype_id === subtypeId && sys.is_active !== false),
      ),
    [catalogSystems],
  );

  const handleSubtypeDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      setSubtypes((prev) => {
        const oldIdx = prev.findIndex((s) => s.id === active.id);
        const newIdx = prev.findIndex((s) => s.id === over.id);
        if (oldIdx < 0 || newIdx < 0) return prev;
        return arrayMove(prev, oldIdx, newIdx);
      });
      setSubtypesDirty(true);
      haptic.selection();
    },
    [haptic],
  );

  const handleSystemDragEnd = useCallback(
    async (subtypeId: number, event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const sorted = systemsForSubtype(subtypeId);
      const oldIdx = sorted.findIndex((s) => s.id === active.id);
      const newIdx = sorted.findIndex((s) => s.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return;
      const reordered = arrayMove(sorted, oldIdx, newIdx);
      setCatalogSystems((prev) => {
        const others = prev.filter((s) => s.subtype_id !== subtypeId);
        return [...others, ...reordered];
      });
      if (!catalogWrite) return;
      try {
        await reorderAdminCatalogSystems(subtypeId, reordered.map((s) => s.id));
        await flushCatalogCache();
        haptic.success();
      } catch (e) {
        alert(formatAxiosAdminError(e, 'Ошибка сохранения порядка систем'));
      }
    },
    [catalogWrite, haptic, systemsForSubtype],
  );

  // ════════════════════════════════════════════════════════════════════════
  return (
    <div className="flex flex-col gap-3 pb-8">
      <BulkPriceModal open={bulkPriceOpen} onClose={() => setBulkPriceOpen(false)} />
      {cropModal}

      <AdminHorizontalTabRail
        variant="compact"
        items={SECTIONS.map((s) => ({ id: s.id, label: s.label, emoji: s.emoji }))}
        value={section}
        onChange={(id) => {
          haptic.selection();
          setSection(id as SectionId);
        }}
        hint="Свайп — Каталог, Пульты, Услуги, Витрина, История"
      />

      {/* ── Pass-through panels ─────────────────────────────────────────── */}
      {section === 'accessories' && <AccessoriesAdminPanel catalogWrite={catalogWrite} token={token} />}
      {section === 'services'    && <ServicesAdminPanel    catalogWrite={catalogWrite} token={token} />}
      {section === 'history'     && <ActivityLogPanel limit={60} />}

      {/* ── Vitrine ─────────────────────────────────────────────────────── */}
      {section === 'vitrine' && (
        <VitrineCategoryPanel
          catalogListPrimed={catalogListPrimed}
          catalogLoading={productsLoading}
          catalogFetchFailed={!!productsError}
          vitrineFromCatalog={catalogVitrineBundle}
          onReloadCatalog={() => void fetchProducts()}
        />
      )}

      {/* ════════════════════════════════════════════════════════════════════
          CATALOG SECTION
      ════════════════════════════════════════════════════════════════════ */}
      {section === 'catalog' && (
        <div className="flex flex-col gap-3">
          <AdminCatalogGuide step={catalogStep} />

          {branches.length > 0 && !branchesLoading && (
            <AdminHorizontalTabRail
              variant="compact"
              items={[
                { id: '__root__', label: 'Все ветки', emoji: '📂' },
                ...branches.map((b) => ({
                  id: String(b.id),
                  label: b.title.length > 22 ? `${b.title.slice(0, 20)}…` : b.title,
                  emoji: CATEGORY_ICON[b.title] || '📦',
                })),
              ]}
              value={activeBranch ? String(activeBranch.id) : '__root__'}
              onChange={(id) => {
                if (id === '__root__') {
                  haptic.selection();
                  setActiveBranch(null);
                  setActiveSubtype(null);
                  setSubtypes([]);
                  return;
                }
                const b = branches.find((x) => String(x.id) === id);
                if (b) void loadSubtypes(b);
              }}
              hint="Ветки каталога · свайп и тап"
            />
          )}

          {activeBranch && subtypes.length > 0 && !subtypesLoading && (
            <AdminHorizontalTabRail
              items={[
                { id: '__all__', label: 'Все подтипы' },
                ...subtypesForAccordion.map((s) => ({
                  id: String(s.id),
                  label: s.title.length > 24 ? `${s.title.slice(0, 22)}…` : s.title,
                })),
              ]}
              value={activeSubtype ? String(activeSubtype.id) : '__all__'}
              onChange={(id) => {
                haptic.selection();
                if (id === '__all__') {
                  setActiveSubtype(null);
                  return;
                }
                const st = subtypes.find((x) => String(x.id) === id);
                if (!st) return;
                setSubtypeAccordionOpen((prev) =>
                  prev.includes(String(st.id)) ? prev : [...prev, String(st.id)],
                );
              }}
              hint={`Подтипы · ${activeBranch.title}`}
            />
          )}

          {/* ── TREE: Navigation panel ──────────────────────────────────── */}
          <div className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>

            {/* Breadcrumb + bulk price button */}
            <div className="flex items-center gap-1.5 px-4 py-3 border-b text-[11px]"
              style={{ borderColor: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}>
              <button
                type="button"
                onClick={() => setBulkPriceOpen(true)}
                className="press ml-auto flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors"
                style={{ background: 'rgba(104,211,145,0.12)', color: '#68D391', border: '1px solid rgba(104,211,145,0.25)' }}
              >
                📈 Массовая цена
              </button>
            </div>
            {/* Breadcrumb bar */}
            <div className="flex items-center gap-1.5 px-4 py-3 border-b text-[11px]"
              style={{ borderColor: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}>
              <button type="button"
                onClick={() => { haptic.selection(); setActiveBranch(null); setActiveSubtype(null); setSubtypes([]); }}
                className="press font-semibold transition-colors"
                style={{ color: !activeBranch ? '#63B3ED' : 'var(--text-3)' }}>
                Каталог
              </button>
              {activeBranch && (<>
                <ChevronRight size={11} />
                <button type="button"
                  onClick={() => { haptic.selection(); setActiveSubtype(null); }}
                  className="press font-semibold transition-colors"
                  style={{ color: !activeSubtype ? '#63B3ED' : 'var(--text-3)' }}>
                  {activeBranch.title}
                </button>
              </>)}
              {activeSubtype && (<>
                <ChevronRight size={11} />
                <span className="font-semibold" style={{ color: '#B794F4' }}>{activeSubtype.title}</span>
              </>)}
              <div className="ml-auto flex gap-1.5">
                {catalogWrite && (
                  <button type="button" disabled={syncBusy}
                    title="Авто-заполнить ветки и подтипы из каталога продуктов"
                    onClick={async () => {
                      setSyncBusy(true); setSyncResult(null);
                      try {
                        const r = await backfillCatalogSubtypes();
                        haptic.success();
                        setSyncResult(`✓ ${r.updated_subtype_id} SKU привязано`);
                        await loadBranches();
                        if (activeBranch) {
                          const rows = await getAdminCatalogSubtypes(activeBranch.id);
                          setSubtypes(Array.isArray(rows) ? rows : []);
                        }
                        await fetchProducts();
                        setTimeout(() => setSyncResult(null), 4000);
                      } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка синхронизации')); }
                      finally { setSyncBusy(false); }
                    }}
                    className="press flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold disabled:opacity-50"
                    style={{ background: 'rgba(99,179,237,0.12)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.3)' }}>
                    {syncBusy ? <Loader2 size={10} className="animate-spin" /> : '⚡'}
                    {syncBusy ? 'Синх…' : 'Синх. из каталога'}
                  </button>
                )}
                {catalogWrite && (
                  <button type="button" title="Сбросить порядок веток по умолчанию"
                    onClick={async () => {
                      setSyncBusy(true);
                      try {
                        await initBranchesSortOrder();
                        const rows = await getAdminCatalogBranches();
                        setBranches(Array.isArray(rows) ? rows : []);
                        haptic.success();
                        setSyncResult('✓ Порядок сброшен по умолчанию');
                        setTimeout(() => setSyncResult(null), 3000);
                      } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка')); }
                      finally { setSyncBusy(false); }
                    }}
                    className="press px-2 py-1.5 rounded-lg text-[10px] font-bold disabled:opacity-50"
                    style={{ background: 'rgba(246,173,85,0.1)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.25)' }}>
                    🔢
                  </button>
                )}
                {catalogWrite && (
                  <button type="button" title="Сбросить кэш каталога"
                    onClick={async () => {
                      setSyncBusy(true);
                      try {
                        await flushCatalogCache();
                        haptic.success();
                        setSyncResult('✓ Кэш сброшен');
                        setTimeout(() => setSyncResult(null), 3000);
                      } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка')); }
                      finally { setSyncBusy(false); }
                    }}
                    className="press px-2 py-1.5 rounded-lg text-[10px] font-bold disabled:opacity-50"
                    style={{ background: 'rgba(154,230,180,0.1)', color: '#9AE6B4', border: '1px solid rgba(154,230,180,0.25)' }}>
                    🗑️
                  </button>
                )}
                <button type="button" onClick={() => { haptic.selection(); void loadBranches(); }}
                  className="press p-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}>
                  <RefreshCw size={11} />
                </button>
              </div>
            </div>

            {/* Sync result toast */}
            {syncResult && (
              <div className="mx-3 mt-2 px-3 py-2 rounded-xl text-[11px] font-semibold"
                style={{ background: 'rgba(72,187,120,0.15)', color: '#68D391', border: '1px solid rgba(72,187,120,0.3)' }}>
                {syncResult}
              </div>
            )}

            {/* ── Data fix hint ──────────────────────────────────────────── */}
            {!activeBranch && !syncBusy && (
              <div className="mx-3 mt-2 mb-1 px-3 py-2 rounded-xl text-[10px] flex items-start gap-2"
                style={{ background: 'rgba(99,179,237,0.06)', border: '1px solid rgba(99,179,237,0.15)', color: 'rgba(99,179,237,0.7)' }}>
                <span className="flex-shrink-0 mt-px">ℹ️</span>
                <span>Если товары в неверных ветках — нажмите <strong>«Синх. из каталога»</strong> для переназначения.</span>
              </div>
            )}

            {/* ── Level 1: All Branches ──────────────────────────────────── */}
            {!activeBranch && (
              <div className="p-3 space-y-1.5">
                {branchesLoading && (
                  <div className="flex items-center gap-2 px-2 py-3 text-xs" style={{ color: 'var(--text-3)' }}>
                    <Loader2 size={14} className="animate-spin" /> Загрузка…
                  </div>
                )}
                {!branchesLoading && branches.length === 0 && (
                  <p className="text-xs px-2 py-3 text-center" style={{ color: 'var(--text-3)' }}>
                    Веток нет — создайте первую ↓
                  </p>
                )}

                {/* Publish banner */}
                {branchesDirty && catalogWrite && (
                  <div className="flex items-center justify-between px-3 py-2.5 rounded-xl mb-1"
                    style={{ background: 'rgba(246,173,85,0.12)', border: '1px solid rgba(246,173,85,0.4)' }}>
                    <span className="text-[11px] font-semibold" style={{ color: '#F6AD55' }}>Порядок изменён — опубликовать?</span>
                    <button type="button" disabled={publishingBranches}
                      onClick={async () => {
                        setPublishingBranches(true);
                        try {
                          await reorderAdminBranches(branches.map(x => x.id));
                          haptic.success();
                          setBranchesDirty(false);
                        } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка')); }
                        finally { setPublishingBranches(false); }
                      }}
                      className="press flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold disabled:opacity-50"
                      style={{ background: 'rgba(246,173,85,0.25)', color: '#F6AD55' }}>
                      {publishingBranches ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                      {publishingBranches ? 'Сохраняю…' : 'Опубликовать'}
                    </button>
                  </div>
                )}

                {!branchesLoading && (
                  <DndContext sensors={dndSensors} collisionDetection={closestCenter}
                    onDragEnd={(event: DragEndEvent) => {
                      const { active, over } = event;
                      if (!over || active.id === over.id) return;
                      setBranches(prev => {
                        const oldIdx = prev.findIndex(b => b.id === active.id);
                        const newIdx = prev.findIndex(b => b.id === over.id);
                        return arrayMove(prev, oldIdx, newIdx);
                      });
                      haptic.selection();
                      setBranchesDirty(true);
                    }}>
                    <SortableContext items={branches.map(b => b.id)} strategy={verticalListSortingStrategy}>
                      {branches.map(b => (
                        <SortableBranchItem key={b.id} branch={b}
                          catalogWrite={catalogWrite}
                          editBranchId={editBranchId}
                          editBranchTitle={editBranchTitle}
                          setEditBranchTitle={setEditBranchTitle}
                          setEditBranchId={setEditBranchId}
                          saveBranchTitle={saveBranchTitle}
                          loadSubtypes={loadSubtypes}
                          deleteBranch={deleteBranch}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
                {catalogWrite && !showNewBranch && (
                  <button type="button" onClick={() => { setShowNewBranch(true); setNewBranchTitle(''); }}
                    className="press w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm transition-all"
                    style={{ background: 'rgba(72,187,120,0.05)', border: '1px dashed rgba(72,187,120,0.35)', color: '#68D391' }}>
                    <Plus size={14} /> Новая ветка
                  </button>
                )}
                {catalogWrite && showNewBranch && (
                  <div className="flex gap-2">
                    <input autoFocus value={newBranchTitle} placeholder="Название (напр. Электрокарнизы)"
                      className="input-glass flex-1 px-3 py-2 rounded-xl text-sm"
                      onChange={e => setNewBranchTitle(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') void createBranch(); if (e.key === 'Escape') setShowNewBranch(false); }} />
                    <button type="button" disabled={newBranchBusy || !newBranchTitle.trim()} onClick={() => void createBranch()}
                      className="press px-3 py-2 rounded-xl font-bold disabled:opacity-40"
                      style={{ background: 'rgba(72,187,120,0.2)', color: '#68D391', border: '1px solid rgba(72,187,120,0.4)' }}>
                      {newBranchBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    </button>
                    <button type="button" onClick={() => setShowNewBranch(false)}
                      className="press px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}>
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Level 2: Subtypes of selected branch ──────────────────── */}
            {activeBranch && (
              <div className="p-3 space-y-1.5">
                {subtypesLoading && (
                  <div className="flex items-center gap-2 px-2 py-3 text-xs" style={{ color: 'var(--text-3)' }}>
                    <Loader2 size={14} className="animate-spin" /> Загрузка подтипов…
                  </div>
                )}
                {!subtypesLoading && subtypes.length === 0 && (
                  <p className="text-xs px-2 py-3 text-center" style={{ color: 'var(--text-3)' }}>
                    Подтипов нет — создайте первый ↓
                  </p>
                )}
                {/* ── Подсказка: как админка влияет на конфигуратор ── */}
                {subtypes.length > 0 && !subtypesLoading && (
                  <div className="px-3 py-2 rounded-xl text-[10px] leading-relaxed"
                    style={{ background: 'rgba(99,179,237,0.06)', border: '1px solid rgba(99,179,237,0.12)', color: 'var(--text-3)' }}>
                    Подтип = карточка в каталоге. <strong style={{ color: '#63B3ED' }}>≡</strong> — порядок на витрине (перетащите, затем «Сохранить порядок»).
                    <strong style={{ color: '#B794F4' }}> Настройки</strong> — мастер из 5 шагов (плитка, калькулятор, цена…). MR‑2234 — отдельно: маркетинг и артикулы.
                  </div>
                )}
                {/* ── Accordion: Подтип → Системы ──────────────────── */}
                {subtypes.length > 0 && !subtypesLoading && (
                  <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02]">
                    <div className="px-4 py-3 border-b border-white/10 text-xs font-bold flex flex-wrap items-center gap-2 justify-between" style={{ color: 'var(--text-2)' }}>
                      <span>🎭 Подтипы ({subtypesForAccordion.length}{hiddenSubtypeCount ? ` · ${hiddenSubtypeCount} скрыто` : ''})</span>
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      {hiddenSubtypeCount > 0 && (
                        <button type="button"
                          onClick={() => { haptic.selection(); setShowHiddenSubtypes(v => !v); }}
                          className="press flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold"
                          style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-3)', border: '1px solid rgba(255,255,255,0.08)' }}>
                          {showHiddenSubtypes ? <EyeOff size={11} /> : <Eye size={11} />}
                          {showHiddenSubtypes ? 'Скрыть' : 'Показать скрытые'}
                        </button>
                      )}
                      {subtypesDirty && catalogWrite && (
                        <button type="button" disabled={publishingSubtypes}
                          onClick={async () => {
                            setPublishingSubtypes(true);
                            try {
                              if (activeBranch) await reorderAdminSubtypes(activeBranch.id, subtypes.map(x => x.id));
                              haptic.success(); setSubtypesDirty(false);
                            } catch (e) { alert(formatAxiosAdminError(e, 'Ошибка')); }
                            finally { setPublishingSubtypes(false); }
                          }}
                          className="press flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold disabled:opacity-50"
                          style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED' }}>
                          {publishingSubtypes ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                          {publishingSubtypes ? 'Сохраняю…' : 'Сохранить порядок'}
                        </button>
                      )}
                      </div>
                    </div>
                    {subtypesLoading ? (
                      <div className="flex items-center gap-2 px-4 py-4 text-xs" style={{ color: 'var(--text-3)' }}>
                        <Loader2 size={12} className="animate-spin" /> Загрузка систем…
                      </div>
                    ) : (
                      <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleSubtypeDragEnd}>
                        <SortableContext
                          items={subtypesForAccordion.map((s) => s.id)}
                          strategy={verticalListSortingStrategy}
                        >
                      <Accordion type="multiple" className="w-full"
                        value={subtypeAccordionOpen}
                        onValueChange={setSubtypeAccordionOpen}
                      >
                        {subtypesForAccordion.map((subtype) => (
                          <SortableSubtypeAccordionItem
                            key={subtype.id}
                            subtype={subtype}
                            subtypeSystems={systemsForSubtype(subtype.id)}
                            catalogWrite={catalogWrite}
                            dndSensors={dndSensors}
                            openSubtype={openSubtype}
                            toggleSubtypeActive={toggleSubtypeActive}
                            deleteSubtype={deleteSubtype}
                            setActiveSubtype={setActiveSubtype}
                            setShowCreateSystem={setShowCreateSystem}
                            setEditingSystem={setEditingSystem}
                            setDeletingSystem={setDeletingSystem}
                            openSystemImageCrop={openSystemImageCrop}
                            onSystemDragEnd={handleSystemDragEnd}
                          />
                        ))}
                      </Accordion>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                )}

                {catalogWrite && !showNewSubtype && (
                  <button type="button" onClick={() => { setShowNewSubtype(true); setNewSubtypeTitle(''); }}
                    className="press w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm"
                    style={{ background: 'rgba(99,179,237,0.05)', border: '1px dashed rgba(99,179,237,0.35)', color: '#63B3ED' }}>
                    <Plus size={14} /> Новый подтип
                  </button>
                )}
                {catalogWrite && showNewSubtype && (
                  <div className="flex gap-2">
                    <input autoFocus value={newSubtypeTitle} placeholder="Название подтипа"
                      className="input-glass flex-1 px-3 py-2 rounded-xl text-sm"
                      onChange={e => setNewSubtypeTitle(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') void createSubtype(); if (e.key === 'Escape') setShowNewSubtype(false); }} />
                    <button type="button" disabled={newSubtypeBusy || !newSubtypeTitle.trim()} onClick={() => void createSubtype()}
                      className="press px-3 py-2 rounded-xl font-bold disabled:opacity-40"
                      style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.4)' }}>
                      {newSubtypeBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    </button>
                    <button type="button" onClick={() => setShowNewSubtype(false)}
                      className="press px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}>
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>

          {activeSubtype && (
            <div
              className="fixed inset-0 z-[42] flex flex-col"
              style={{ background: '#0A192F', paddingTop: 'env(safe-area-inset-top)' }}
            >
              <header
                className="flex items-center gap-2 px-3 py-3 border-b shrink-0"
                style={{ borderColor: 'rgba(255,255,255,0.08)' }}
              >
                <button
                  type="button"
                  onClick={() => { haptic.selection(); setActiveSubtype(null); }}
                  className="press flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-semibold"
                  style={{ background: 'rgba(255,255,255,0.06)', color: '#63B3ED' }}
                >
                  <ChevronLeft size={16} />
                  Назад к списку
                </button>
                <div className="flex-1 min-w-0 text-right">
                  <p className="text-sm font-bold truncate" style={{ color: 'var(--text-1)' }}>{activeSubtype.title}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>Мастер · 5 шагов</p>
                </div>
              </header>
              <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-3 space-y-3">
                <SubtypeSetupPanel
                  activeSubtype={activeSubtype}
                  catalogWrite={catalogWrite}
                  skuTotal={skuTotal}
                  schemaForm={schemaForm}
                  setSchemaForm={setSchemaForm}
                  schemaSaving={schemaSaving}
                  onSave={() => void saveSchema()}
                  onToggleActive={() => void toggleSubtypeActive(activeSubtype)}
                  systemIdDraft={systemIdDraft}
                  setSystemIdDraft={setSystemIdDraft}
                  catalogSystems={catalogSystems}
                  subtypeImgRef={subtypeImgRef}
                  subtypeImgUploading={subtypeImgUploading}
                  onSubtypeImageSelected={(f) => void openSubtypeImageCrop(f)}
                  formulaDraft={formulaDraft}
                  setFormulaDraft={setFormulaDraft}
                  formulaPreview={formulaPreview}
                  setFormulaPreview={setFormulaPreview}
                  newColorInput={newColorInput}
                  setNewColorInput={setNewColorInput}
                  catAssignInput={catAssignInput}
                  setCatAssignInput={setCatAssignInput}
                  assignByCatBusy={assignByCatBusy}
                  onAssignByCategory={async () => {
                    if (!activeSubtype) return;
                    setAssignByCatBusy(true);
                    try {
                      const res = await bulkAssignByCategory(catAssignInput.trim(), null, activeSubtype.id);
                      haptic.success();
                      setSkuTotal(res.updated);
                      setCatAssignInput('');
                      await fetchProducts();
                    } catch (e) {
                      alert(formatAxiosAdminError(e, 'Ошибка'));
                    } finally {
                      setAssignByCatBusy(false);
                    }
                  }}
                  batchBusy={batchBusy}
                  onBatchProtocol={async (pr) => {
                    if (!activeSubtype) return;
                    setBatchBusy(true);
                    try {
                      const { postAdminSubtypeBatchSkus } = await import('@/api');
                      await postAdminSubtypeBatchSkus(activeSubtype.id, {
                        set: { protocol: pr },
                        regenerate_external_id: false,
                      });
                      haptic.selection();
                    } catch (e) {
                      alert(formatAxiosAdminError(e, 'Ошибка'));
                    } finally {
                      setBatchBusy(false);
                    }
                  }}
                />

                {/* ── SKU в подтипе ── */}
                <div className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>

            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b"
              style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>
                  {activeSubtype ? activeSubtype.title : activeBranch ? activeBranch.title : 'Все позиции'}
                </p>
                <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>
                  {filteredProducts.filter(p => p.is_active).length} активных · {filteredProducts.length} всего
                </p>
              </div>
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-3)' }} />
                <input type="text" placeholder="Поиск…" value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="input-glass pl-7 pr-3 py-1.5 rounded-xl text-xs w-32 sm:w-44" />
              </div>
              <button type="button" onClick={() => void fetchProducts()}
                className="press p-2 rounded-xl flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}>
                <RefreshCw size={13} className={productsLoading ? 'animate-spin' : ''} />
              </button>
            </div>

            {/* Bulk action bar */}
            {catalogWrite && selectedIds.size > 0 && (
              <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b text-[11px]"
                style={{ background: 'rgba(99,179,237,0.05)', borderColor: 'rgba(99,179,237,0.18)' }}>
                <span className="font-bold" style={{ color: '#63B3ED' }}>{selectedIds.size} выбрано</span>
                <button onClick={() => setSelectedIds(new Set())}
                  className="press px-2 py-0.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}>Снять</button>
                <button disabled={bulkBusy}
                  onClick={async () => {
                    setBulkBusy(true);
                    try { await bulkToggleActive([...selectedIds], true); setProducts(p => p.map(x => selectedIds.has(x.id) ? { ...x, is_active: true } : x)); setSelectedIds(new Set()); }
                    catch (e) { alert(formatAxiosAdminError(e, '')); }
                    finally { setBulkBusy(false); }
                  }}
                  className="press px-2 py-0.5 rounded-lg font-bold disabled:opacity-40"
                  style={{ background: 'rgba(72,187,120,0.18)', color: '#68D391', border: '1px solid rgba(72,187,120,0.35)' }}>✓ Активировать</button>
                <button disabled={bulkBusy}
                  onClick={async () => {
                    setBulkBusy(true);
                    try { await bulkToggleActive([...selectedIds], false); setProducts(p => p.map(x => selectedIds.has(x.id) ? { ...x, is_active: false } : x)); setSelectedIds(new Set()); }
                    catch (e) { alert(formatAxiosAdminError(e, '')); }
                    finally { setBulkBusy(false); }
                  }}
                  className="press px-2 py-0.5 rounded-lg font-bold disabled:opacity-40"
                  style={{ background: 'rgba(252,129,74,0.18)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}>✗ Скрыть</button>
                {activeSubtype && (
                  <button disabled={bulkBusy}
                    onClick={async () => {
                      setBulkBusy(true);
                      try { await bulkAssignSubtype([...selectedIds], activeSubtype.id); setProducts(p => p.map(x => selectedIds.has(x.id) ? { ...x, subtype_id: activeSubtype.id } : x)); setSelectedIds(new Set()); }
                      catch (e) { alert(formatAxiosAdminError(e, '')); }
                      finally { setBulkBusy(false); }
                    }}
                    className="press px-2 py-0.5 rounded-lg font-bold disabled:opacity-40"
                    style={{ background: 'rgba(159,122,234,0.18)', color: '#B794F4', border: '1px solid rgba(159,122,234,0.35)' }}>
                    → В подтип «{activeSubtype.title}»
                  </button>
                )}
              </div>
            )}

            {/* Loading / error */}
            {(productsLoading || subtypeSkusLoading) && <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin" style={{ color: 'var(--cyan)' }} /></div>}
            {productsError && (
              <div className="mx-4 my-3 rounded-xl px-3 py-2.5 text-xs"
                style={{ background: 'rgba(252,129,74,0.1)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.3)' }}>
                {productsError}
              </div>
            )}

            {/* Column header */}
            {!productsLoading && filteredProducts.length > 0 && (
              <div className="grid px-4 py-2 text-[10px] font-bold uppercase tracking-wider border-b"
                style={{
                  gridTemplateColumns: catalogWrite ? '24px 1fr 90px 52px 60px' : '1fr 90px 52px 60px',
                  color: 'var(--text-3)', borderColor: 'rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)'
                }}>
                {catalogWrite && <span><input type="checkbox"
                  checked={filteredProducts.every(p => selectedIds.has(p.id))}
                  onChange={e => setSelectedIds(e.target.checked ? new Set(filteredProducts.map(p => p.id)) : new Set())} /></span>}
                <span>Позиция</span>
                <span className="text-right pr-2">Цена</span>
                <span className="text-center">Статус</span>
                <span></span>
              </div>
            )}

            {/* Rows */}
            <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
              {!productsLoading && filteredProducts.length === 0 && !productsError && (
                <p className="text-center py-8 text-xs" style={{ color: 'var(--text-3)' }}>
                  {activeSubtype ? `В «${activeSubtype.title}» нет позиций.` : 'Нет позиций.'}
                </p>
              )}
              {filteredProducts.map(p => {
                const draft = productDrafts[p.id] ?? {};
                const basePrice = draft.base_price ?? readBasePrice(p.price_matrix);
                const isChanged = Object.keys(draft).length > 0;
                const isActive = draft.is_active ?? p.is_active;
                const isExpanded = expandedProduct === p.id;
                const isSelected = selectedIds.has(p.id);

                return (
                  <div key={p.id} style={isSelected ? { background: 'rgba(99,179,237,0.04)' } : {}}>
                    <div
                      className="grid items-center px-4 py-3 gap-2 hover:bg-white/[0.02] transition-colors"
                      style={{ gridTemplateColumns: catalogWrite ? '24px 1fr 90px 52px 60px' : '1fr 90px 52px 60px' }}>
                      {catalogWrite && (
                        <input type="checkbox" checked={isSelected}
                          onChange={e => setSelectedIds(prev => { const n = new Set(prev); e.target.checked ? n.add(p.id) : n.delete(p.id); return n; })} />
                      )}
                      <div className="min-w-0">
                        <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-1)' }}>
                          {draft.name ?? p.name}
                        </p>
                        <p className="text-[10px] font-mono truncate mt-0.5" style={{ color: '#63B3ED' }}>
                          {p.external_id || '—'}
                        </p>
                      </div>
                      <div className="text-right pr-2">
                        {catalogWrite ? (
                          <input type="number" min={0}
                            className="input-glass w-full px-2 py-1.5 rounded-lg text-xs text-right"
                            value={basePrice || ''}
                            onChange={e => setProductDrafts(prev => ({ ...prev, [p.id]: { ...draft, base_price: Number(e.target.value) } }))}
                            placeholder="₽" />
                        ) : (
                          <span className="text-xs font-bold" style={{ color: 'var(--text-1)' }}>
                            {basePrice > 0 ? `${basePrice.toLocaleString('ru')} ₽` : '—'}
                          </span>
                        )}
                      </div>
                      <div className="flex justify-center">
                        <button type="button" disabled={!catalogWrite}
                          onClick={() => { if (!catalogWrite) return; haptic.selection(); setProductDrafts(prev => ({ ...prev, [p.id]: { ...draft, is_active: !isActive } })); }}
                          className="press text-[10px] font-bold px-2 py-1 rounded-full"
                          style={isActive
                            ? { background: 'rgba(72,187,120,0.18)', color: '#68D391', border: '1px solid rgba(72,187,120,0.35)' }
                            : { background: 'rgba(255,255,255,0.05)', color: 'var(--text-3)', border: '1px solid rgba(255,255,255,0.1)' }}>
                          {isActive ? '✓' : '✗'}
                        </button>
                      </div>
                      <div className="flex items-center justify-end gap-1.5">
                        {catalogWrite && isChanged && (
                          <button type="button" disabled={updatingProduct === p.id}
                            onClick={() => { haptic.selection(); void saveProduct(p.id); }}
                            className="press text-[10px] px-1.5 py-1 rounded-lg font-bold disabled:opacity-50"
                            style={{ background: 'rgba(72,187,120,0.2)', color: '#68D391' }}>
                            {updatingProduct === p.id ? '…' : '💾'}
                          </button>
                        )}
                        <button type="button"
                          onClick={() => { haptic.selection(); setExpandedProduct(isExpanded ? null : p.id); }}
                          className="press p-1.5 rounded-lg text-[11px]"
                          style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}>
                          {isExpanded ? '▲' : '▾'}
                        </button>
                      </div>
                    </div>

                    {/* Expanded row */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-3 space-y-4 border-t"
                        style={{ borderColor: 'rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.01)' }}>

                        {/* Control type (protocol) */}
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-3)' }}>📶 Тип управления</p>
                          <div className="flex flex-wrap gap-1.5">
                            {ALL_PROTOCOLS.map(pr => {
                              const th = PROTOCOL_THEME[pr];
                              const current = draft.protocol ?? p.protocol ?? '';
                              const on = current === pr;
                              return (
                                <button key={pr} type="button" disabled={!catalogWrite}
                                  onClick={() => setProductDrafts(prev => ({ ...prev, [p.id]: { ...draft, protocol: on ? '' : pr } }))}
                                  className="press flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[10px] font-semibold disabled:opacity-40"
                                  style={on ? { background: th.bg, color: th.text, border: `1px solid ${th.border}` }
                                    : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-3)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                  {th.icon} {th.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Color + Power row */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-3)' }}>🎨 Цвет</p>
                            <input disabled={!catalogWrite}
                              className="input-glass w-full px-2.5 py-2 rounded-xl text-xs"
                              placeholder="Белый / Чёрный…"
                              value={draft.color ?? p.color ?? ''}
                              onChange={e => setProductDrafts(prev => ({ ...prev, [p.id]: { ...draft, color: e.target.value } }))} />
                          </div>
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-3)' }}>⚡ Вид подключения</p>
                            <div className="flex gap-2">
                              {[{ val: false, label: '220В', bg: 'rgba(99,179,237,0.15)', color: '#63B3ED', border: 'rgba(99,179,237,0.4)' }, { val: true, label: 'АКБ', bg: 'rgba(72,187,120,0.15)', color: '#68D391', border: 'rgba(72,187,120,0.4)' }].map(opt => {
                                const current = draft.is_battery_powered ?? p.is_battery_powered ?? false;
                                const on = current === opt.val;
                                return (
                                  <button key={String(opt.val)} type="button" disabled={!catalogWrite}
                                    onClick={() => setProductDrafts(prev => ({ ...prev, [p.id]: { ...draft, is_battery_powered: opt.val } }))}
                                    className="press flex-1 py-2 rounded-xl text-[11px] font-bold disabled:opacity-40"
                                    style={on ? { background: opt.bg, color: opt.color, border: `1px solid ${opt.border}` }
                                      : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-3)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Motor model + Price row */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-3)' }}>⚙️ Модель мотора</p>
                            <input disabled={!catalogWrite}
                              className="input-glass w-full px-2.5 py-2 rounded-xl text-xs"
                              placeholder="напр. Somfy RTS 50"
                              value={draft.motor_model ?? p.motor_model ?? ''}
                              onChange={e => setProductDrafts(prev => ({ ...prev, [p.id]: { ...draft, motor_model: e.target.value } }))} />
                          </div>
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-3)' }}>💰 Базовая цена (₽)</p>
                            <input type="number" min={0} step={100} disabled={!catalogWrite}
                              className="input-glass w-full px-2.5 py-2 rounded-xl text-xs text-right"
                              placeholder="0"
                              value={(draft.base_price ?? readBasePrice(p.price_matrix)) || ''}
                              onChange={e => setProductDrafts(prev => ({ ...prev, [p.id]: { ...draft, base_price: Number(e.target.value) } }))} />
                          </div>
                        </div>

                        {/* Subtype assign */}
                        {catalogWrite && (
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-3)' }}>🏷 Подтип</p>
                            <select disabled={assigningSubtype === p.id}
                              value={(draft.subtype_id ?? p.subtype_id) ?? ''}
                              onChange={async e => {
                                const sid = e.target.value === '' ? null : Number(e.target.value);
                                setAssigningSubtype(p.id);
                                try {
                                  await assignProductSubtype(p.id, sid);
                                  setProducts(prev => prev.map(x => x.id === p.id ? { ...x, subtype_id: sid } : x));
                                  haptic.success();
                                } catch (err) { alert(formatAxiosAdminError(err, 'Ошибка')); }
                                finally { setAssigningSubtype(null); }
                              }}
                              className="input-glass w-full px-3 py-2 rounded-xl text-xs"
                              style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-1)', border: '1px solid var(--border-subtle)' }}>
                              <option value="">— Не назначен —</option>
                              {subtypes.map(s => <option key={s.id} value={String(s.id)}>{s.title}</option>)}
                            </select>
                          </div>
                        )}

                        {/* Description */}
                        {catalogWrite && (
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-3)' }}>📝 Описание</p>
                            <textarea rows={2}
                              className="input-glass w-full px-3 py-2 rounded-xl text-xs"
                              value={draft.description ?? p.description ?? ''}
                              onChange={e => setProductDrafts(prev => ({ ...prev, [p.id]: { ...draft, description: e.target.value } }))}
                              placeholder="Краткое описание позиции" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
                </div>
              </div>
            </div>
          )}




        </div>
      )}

      {/* PIM System Editor Modal */}
      <SystemEditor
        system={editingSystem}
        isOpen={!!editingSystem}
        onClose={() => setEditingSystem(null)}
        onDuplicate={(_newSystem: { id: number; slug: string; title: string }) => {
          // Refresh catalog systems list after duplication
          void getAdminCatalogBranches().then((branches) => {
            setBranches(branches);
          });
        }}
      />

      {/* Create System Dialog */}
      {showCreateSystem && (
        <CreateSystemDialogInline
          branch={activeBranch}
          subtype={activeSubtype}
          onClose={() => setShowCreateSystem(false)}
          onCreated={(system: AdminCatalogSystem) => {
            setCatalogSystems(prev => [...prev, system]);
            setShowCreateSystem(false);
            haptic.success();
          }}
        />
      )}

      {/* Delete System Confirmation */}
      {deletingSystem && (
        <DeleteSystemDialogInline
          system={deletingSystem}
          onClose={() => setDeletingSystem(null)}
          onDeleted={() => {
            setCatalogSystems(prev => prev.filter(s => s.id !== deletingSystem.id));
            setDeletingSystem(null);
            haptic.success();
          }}
        />
      )}
    </div>
  );
}

// ===== INLINE DIALOG COMPONENTS =====

interface CreateSystemDialogInlineProps {
  branch: AdminCatalogBranchRow | null;
  subtype: { id: number; title: string; catalog_system_id?: number | null } | null;
  onClose: () => void;
  onCreated: (system: AdminCatalogSystem) => void;
}

function CreateSystemDialogInline({ branch, subtype, onClose, onCreated }: CreateSystemDialogInlineProps) {
  const haptic = useHaptic();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [category, setCategory] = useState(branch?.slug?.replace(/-/g, '_') || '');
  const [subcategory, setSubcategory] = useState(subtype?.title || '');
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateSlug = (text: string) => {
    return text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 50);
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slug || slug === generateSlug(title)) {
      setSlug(generateSlug(value));
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !slug.trim()) {
      setError('Название и код обязательны');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await api.post('/admin/catalog-systems', {
        title: title.trim(),
        slug: slug.trim(),
        category: category || branch?.slug,
        subcategory: subcategory || subtype?.title,
        is_active: isActive,
        short_description: '',
      });
      const newSystem = response.data;
      if (subtype?.id && newSystem.id) {
        await api.patch(`/admin/catalog-subtypes/${subtype.id}`, { catalog_system_id: newSystem.id });
      }
      onCreated(newSystem as AdminCatalogSystem);
      haptic.success();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка при создании системы');
      haptic.error();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#1a1f2e] shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white/90">Создать систему</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-white/50 transition-colors"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
          <div className="space-y-2">
            <label className="text-xs font-medium text-white/50 uppercase tracking-wide">Название системы</label>
            <input type="text" value={title} onChange={e => handleTitleChange(e.target.value)} placeholder="например: Somfy Sonesse 40"
              className="w-full px-3 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] focus:border-[#68D391]/50 focus:outline-none text-white placeholder-white/30" disabled={isSubmitting} />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-white/50 uppercase tracking-wide">Код (slug)</label>
            <input type="text" value={slug} onChange={e => setSlug(e.target.value)} placeholder="somfy-sonesse-40"
              className="w-full px-3 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] focus:border-[#68D391]/50 focus:outline-none text-white placeholder-white/30 font-mono" disabled={isSubmitting} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-xs font-medium text-white/50 uppercase tracking-wide">Категория</label>
              <input type="text" value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] focus:border-[#68D391]/50 focus:outline-none text-white" disabled={isSubmitting} />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-white/50 uppercase tracking-wide">Подкатегория</label>
              <input type="text" value={subcategory} onChange={e => setSubcategory(e.target.value)} className="w-full px-3 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] focus:border-[#68D391]/50 focus:outline-none text-white" disabled={isSubmitting} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="rounded border-white/20 bg-white/[0.04]" disabled={isSubmitting} />
            Активна (видна в конфигураторе)
          </label>
          <div className="pt-2 flex gap-3">
            <button onClick={onClose} disabled={isSubmitting} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:bg-white/5 transition-colors">Отмена</button>
            <button onClick={handleSubmit} disabled={isSubmitting || !title.trim()} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium bg-[#68D391]/15 text-[#68D391] hover:bg-[#68D391]/25 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {isSubmitting ? <><Loader2 size={16} className="animate-spin" />Создание...</> : <><Plus size={16} />Создать</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface DeleteSystemDialogInlineProps {
  system: AdminCatalogSystem;
  onClose: () => void;
  onDeleted: () => void;
}

function DeleteSystemDialogInline({ system, onClose, onDeleted }: DeleteSystemDialogInlineProps) {
  const haptic = useHaptic();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await api.delete(`/admin/catalog-systems/${system.id}`);
      onDeleted();
      haptic.success();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка при удалении системы');
      haptic.error();
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#1a1f2e] shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white/90 flex items-center gap-2"><AlertTriangle size={20} className="text-red-400" />Удалить систему?</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-white/50 transition-colors"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
          <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
            <p className="text-sm font-medium text-white/90">{system.title}</p>
            <p className="text-xs text-white/50 mt-1">#{system.id} • {system.slug}</p>
          </div>
          <p className="text-sm text-white/60">Система будет удалена из каталога. Это действие нельзя отменить.<br /><br /><span className="text-red-400">Внимание:</span> если система привязана к подтипам, связи будут разорваны.</p>
          <div className="pt-2 flex gap-3">
            <button onClick={onClose} disabled={isDeleting} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:bg-white/5 transition-colors">Отмена</button>
            <button onClick={handleDelete} disabled={isDeleting} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {isDeleting ? <><Loader2 size={16} className="animate-spin" />Удаление...</> : <><Trash2 size={16} />Удалить</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
